import { ProductLine } from '@kouto/types';
import useListings from 'api/listing/useListings';
import { useLandingPageRouteParams } from 'hooks/useLandingPageRouteParams';
import { NormalizedPublicListing } from 'types/listingV3';
import {
  formatListingsToGroupActivateByDate,
  useListingsV2Params,
} from 'utils/listingsHelpers';
import { LANDING_PAGE_TITLE_MAP } from '../consts';
import { useLandingPageAnalyticsView } from './useLandingPageAnalyticsView';

interface UseProductLandingListingsResult {
  readonly isError: boolean;
  readonly isLoading: boolean;
  /** Display listings — for Activate the raw sessions are grouped by date (one card per event
      on `/`, fanned out into per-event cards on `/search`); other product lines pass through
      untouched. */
  readonly listings: NormalizedPublicListing[];
  readonly loadMore: () => void;
  /** Localized page-title key derived from the product line (falls back to `hostProductPageTitle`
      for products without a dedicated title). */
  readonly pageTitle: string;
}

/**
 * Data layer for ProductLandingPage and its ScheduleView variant. Fetches a product-scoped
 * listings feed, wires analytics, applies Activate-specific grouping, and resolves the page
 * title from the product→title map. Both variants share this pipeline verbatim.
 */
const useProductLandingListings = (
  product: ProductLine,
): UseProductLandingListingsResult => {
  const { isResults } = useLandingPageRouteParams();
  const showMultipleCardsPerEvent =
    (isResults && product === ProductLine.ACTIVATE) ?? false;
  const baseParams = useListingsV2Params();

  const { listings, loadMore, isLoading, isError } = useListings({
    v2Params: {
      ...baseParams,
      productLines: [product],
    },
    v3Params: {
      ...baseParams,
      isUnlisted: baseParams.isUnlisted === 'true',
      productLines: [product],
    },
  });

  useLandingPageAnalyticsView({
    listings,
    isReadyToTrackAnalytics: !isLoading,
  });

  const displayListings =
    product === ProductLine.ACTIVATE
      ? formatListingsToGroupActivateByDate(
          listings,
          showMultipleCardsPerEvent,
          true,
        )
      : listings;

  return {
    isError,
    isLoading,
    listings: displayListings,
    loadMore,
    pageTitle: LANDING_PAGE_TITLE_MAP[product] ?? 'hostProductPageTitle',
  };
};

export default useProductLandingListings;
